import { TOUCH_TARGET } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

// Every hover has a `:focus-visible` twin, so a keyboard user sees the same
// change a pointer does.
const INSET_GREY =
  'size-8 bg-muted text-muted-foreground hover:bg-border hover:text-foreground focus-visible:bg-border focus-visible:text-foreground'

const VARIANT_CLASSES = {
  // The raised grey, not the inset one: `bg-muted` on a card is 1.02:1, a
  // hover nobody sees. `bg-secondary` shows on both the page and a card.
  ghost: 'size-9 rounded-full hover:bg-secondary focus-visible:bg-secondary',
  // Keeps its fill and lifts the edge, as every other boxed control does.
  outline:
    'size-9 rounded-full border border-border-subtle bg-popover hover:border-border-strong focus-visible:border-border-strong',
  // Figma draws no edge on it. The transparent one is there so hover can draw
  // the strong hairline every boxed control takes without moving the glyph.
  toolbar:
    'size-10 rounded-sm border border-transparent bg-secondary hover:border-border-strong focus-visible:border-border-strong',
  square: INSET_GREY,
  muted: `${INSET_GREY} rounded-full`,
} as const

export type IconButtonVariant = keyof typeof VARIANT_CLASSES

/**
 * The one place an icon control's skin is written. IconButton (a `<button>`)
 * and IconLink (an `<a>`) both wear it, so the link twin cannot drift from the
 * button it has to look like: a toolbar row mixing the two reads as one row.
 *
 * Its own module so IconButton.tsx and IconLink.tsx export only components
 * (Fast Refresh), the split `button-variants.ts` makes.
 */
export const iconButtonClass = (variant: IconButtonVariant, className?: string): string =>
  cn(
    // sz:kit — the mock's control ring (A2) and press (A1): white, and a .97 scale where motion is
    // welcome. The fill/edge hovers below are already the mock's shift, not a fade.
    'relative inline-flex shrink-0 cursor-pointer items-center justify-center transition-[color,background-color,border-color,scale] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control motion-safe:active:scale-97 disabled:pointer-events-none disabled:opacity-38',
    // Invisible and untouched by the ring, which follows the button.
    TOUCH_TARGET,
    'touch-manipulation',
    VARIANT_CLASSES[variant],
    className,
  )
